import {
    makeStyles,
    Menu,
    MenuButton,
    MenuGroup,
    MenuGroupHeader,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { History20Regular } from '@fluentui/react-icons';
import { ColumnModel, GridComponent, IEditCell, IIndex } from '@syncfusion/ej2-react-grids';
import { useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useNavigate } from 'react-router';
import { useGetTaxReturnById, useGetWhitePaperReportPreviousVersions } from '../../../api/report/report';
import { GridStateContextProvider } from '../../../contexts/gridStateContext';
import { useFormView } from '../../../contexts/useFormView';
import { useUserSettings } from '../../../contexts/useUserSettings';
import { IFilterItem } from '../../../data-types/filterItem';
import { ICurrentCellEventArgs } from '../../../data-types/ICurrentCellEventArgs';
import { IRowInfo } from '../../../data-types/IRowInfo';
import { ISelectableGrid } from '../../../data-types/ISelectableGrid';
import { createSelectedWhitePaperCell } from '../../../data-types/selectedWhitePaperCell';
import { useCellCommentsRouteDetails } from '../../../hooks/useCellCommentsRouteDetails';
import { useDrillDown } from '../../../hooks/useDrillDown';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useGridScroll } from '../../../hooks/useGridScroll';
import { usePerformanceMetricsForTable } from '../../../hooks/usePerformanceMetricsForTable';
import { usePreventNavigation } from '../../../hooks/usePreventNavigation';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { useSelectedEntities } from '../../../hooks/useSelectedEntities';
import { useTaxReturnCalculationState } from '../../../hooks/useTaxReturnCalculationState';
import {
    CalculationExecutionStatus,
    IWhitePaperColumn,
    IWhitePaperMappedRowDictionary,
    IWhitePaperReport,
    IWhitePaperReportChanges,
    IWhitePaperRow,
    Report,
} from '../../../model';

import { useGetMetadataForReport } from '../../../api/collaboration/collaboration';
import { useIsFeatureEnabled } from '../../../api/features/features';
import Pillar2Panel from '../../../features/pillar2/Pillar2Panel';
import { usePillar2FeatureFlag } from '../../../features/pillar2/shared/usePillar2FeatureFlag';
import { useIsCollaborationUIEnabled } from '../../../hooks/useIsCollaborationUIEnabled';
import { useReportFieldCommentsDataSource } from '../../../hooks/useReportFieldCommentsDataSource';
import { useReportFieldCommentsMetadata } from '../../../hooks/useReportFieldCommentsMetadata';
import { useTaxReturnEventHandler } from '../../../hooks/useTaxReturnEventHandler';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useWhitePaperReportCache } from '../../../hooks/useWhitePaperReportCache';
import { REDIRECTED_ACTION_NAME, REDIRECTED_EVENT_NAME } from '../../../telemetry/events';
import { useEventTracker } from '../../../telemetry/hooks/useEventTracker';
import { useUtmTracking } from '../../../telemetry/hooks/useUtmTracking';
import { isResponseArrayValid } from '../../../utils/ApiClientUtils';
import {
    getColumnHeaderTemplate,
    getColumnTemplate,
    getColumnUid,
    getColumnWidth,
    isColumnVisibleAfterEntityFilter,
} from '../../../utils/ColumnUtils';
import { useDataManager } from '../../../utils/DataManagerUtils';
import { isDevelopment } from '../../../utils/EnvironmentUtils';
import { AutoPostAmountsEnabled, FilteredEnterAmountsEnabled } from '../../../utils/FeatureFlags.constants';
import { getEntitiesFilterForColumns, getSectionsFilterItem } from '../../../utils/FilterUtils';
import {
    CLICKABLE_COLUMN_START_INDEX,
    getColumnFieldNameByIndex,
    getDynamicFieldName,
    getDynamicFieldNameParts,
    getReportVersionLabelText,
    getVisibleEntityColumnNames,
    isDrillDownContextMenuAllowed,
    isInputRow,
    isMenuEnabledForTableColumn,
    isMultiColumnReport,
    shouldExecuteRerun,
    shouldFetch,
    updateSubtotalByEntitiesValue,
} from '../../../utils/ReportUtils';
import { ContextMenuItemDisplayState, ISyncfusionContextMenuItemProps } from '../../../utils/SyncfusionContextMenuUtils';
import { TOTAL_ROW_NUMBER } from '../../../utils/Table.constants';
import { getWhitePaperReportHeaderTemplate, setFocusOnSyncfusionGridCell } from '../../../utils/TableUtils';
import { getFilteredTOCItemsByCondition } from '../../../utils/TaxReturnTOCUtils';
import { shouldFreezeColumnByHeaderName } from '../../../utils/TreegridUtils';
import { VIEW_REPORT_URL } from '../../../utils/Urls.constant';
import GridSearchBar from '../../common/GridSearchBar';
import LoadingSpinner from '../../common/LoadingSpinner';
import CalculationDiagnosticsButton from '../../diagnostics/CalculationDiagnosticsButton';
import EnterAmountsDialog, { IEnterAmountsDialogProps } from '../../enter-amounts-dialog/EnterAmountsDialog';
import FilterButton from '../../filters/filter-button/FilterButton';
import { CommentsPanelWrapper } from '../../sidebar/CommentsPanelWrapper';
import ReportDrillDown, { IReportDrillDownOnClose } from '../../sidebar/ReportDrillDown';
import ActionBarButtons from '../action-buttons/actionBarButtons';
import CalculateButton from '../action-buttons/calculateButton';
import Pillar2ReportButton from '../action-buttons/pillar2ReportButton';
import CommonIndividualReportTable, {
    descriptionTemplate,
    ICommonIndividualReportTableContextMenuProps,
} from '../common-individual-report-table/CommonIndividualReportTable';
import ScrollToSectionDropdown from '../common-individual-report-table/ScrollToSectionDropdown';

const useWrapperStyles = makeStyles({
    reportLinesWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    resolvingSpinner: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
    },
});

const getHeaderTextBasedOnColumnId = (column: IWhitePaperColumn): string => {
    return column.description ?? column.headingFirstLine ?? '';
};

const useHistoryMenuStyles = makeStyles({
    popover: {
        maxWidth: '350px',
        width: '350px',
        minWidth: '350px',
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        minHeight: '36px',
        fontSize: '13px',
        fontWeight: '600',
        paddingLeft: '24px',
        paddingRight: '24px',
        maxWidth: '100%',
        width: '100%',
    },
});

const isDrillDownAllowed = (cellIndex?: IIndex) => {
    if (cellIndex && cellIndex.cellIndex) {
        return isMenuEnabledForTableColumn(cellIndex.cellIndex);
    }
    return false;
};

export const getWhitePaperReportColumnModels = (
    report: IWhitePaperReport | undefined,
    frozenColumnHeaders?: string[],
    filteredColumns?: IFilterItem[]
): ColumnModel[] => {
    //TODO: move this to a shared location so reportlines, comparereportlines, and quick add adjustments
    // can all use this model
    const columns: ColumnModel[] = [];
    const numericParams: IEditCell = {
        params: {
            decimals: 0,
            format: 'N',
            validateDecimalOnType: true,
            showSpinButton: false,
            htmlAttributes: { maxlength: '15' },
        },
    };
    const isMultiColumn: boolean = isMultiColumnReport(report);

    report?.columns?.forEach((col) => {
        const fieldName = getDynamicFieldName(col.fieldName, '.current');
        const isVisible = isColumnVisibleAfterEntityFilter(filteredColumns ?? [], col);
        const headerText = getHeaderTextBasedOnColumnId(col);
        const shouldFreeze = isMultiColumn && frozenColumnHeaders && shouldFreezeColumnByHeaderName(headerText, frozenColumnHeaders);
        const columnNumber = report?.columns?.findIndex((c) => c === col) ?? 0;
        columns.push({
            width: getColumnWidth(col.longestValueLength),
            field: fieldName,
            headerText: headerText,
            headerTemplate: getWhitePaperReportHeaderTemplate(col), //TODO: see if this is actually needed, or if we can just use reportHeaderTemplate
            template: getColumnTemplate(fieldName, columnNumber),
            headerTextAlign: 'Right',
            textAlign: 'Right',
            customAttributes: { class: 'white-paper-report-clickable-value-cell' },
            isFrozen: shouldFreeze,
            freeze: shouldFreeze ? 'Left' : 'None',
            allowResizing: true,
            autoFit: !isMultiColumn,
            visible: isVisible,
            edit: numericParams,
            editType: 'numericedit',
            uid: getColumnUid(col),
        } as ColumnModel);
    });

    return columns;
};

const frozenColumnHeaders: string[] = ['total'];
export const getReportLinesColumns = (
    report: IWhitePaperReport | undefined,
    t: TFunction<'translation', undefined, 'translation'>,
    frozenColumnHeaders: string[],
    filteredColumns?: IFilterItem[]
) => {
    const isMultiColumns: boolean = isMultiColumnReport(report);
    const rowNumberColumnModel: ColumnModel = {
        field: 'rowNumber',
        width: '100px',
        headerText: 'Row Number',
        isFrozen: isMultiColumns,
        freeze: isMultiColumns ? 'Left' : 'None',
        allowResizing: true,
        autoFit: false,
        allowEditing: false,
        isPrimaryKey: true,
        visible: isDevelopment(),
    };
    const descriptionColumnModel: ColumnModel = {
        field: 'description',
        maxWidth: '3130px',
        width: '450px',
        headerText: t('description').toString(),
        isFrozen: isMultiColumns,
        freeze: isMultiColumns ? 'Left' : 'None',
        headerTemplate: getColumnHeaderTemplate,
        template: descriptionTemplate,
        customAttributes: { class: 'white-paper-report-customizable-cell' },
        allowEditing: false,
        visible: true,
        editTemplate: (props: IWhitePaperRow) => <span style={{ paddingLeft: '23px' }}>{props.description}</span>,
    };
    return [rowNumberColumnModel, descriptionColumnModel, ...getWhitePaperReportColumnModels(report, frozenColumnHeaders, filteredColumns)];
};

export interface IReportLinesTableProps {
    navigateToCompareReport: (versionId: string, selectedEntities: string[]) => void;
    reportId: number;
}

const ReportLinesTable: FC<IReportLinesTableProps> = (props: IReportLinesTableProps) => {
    const { setShouldBlockNavigation } = usePreventNavigation();

    const { trackScrollStarted, trackScrollFinished, trackRenderFinished, resetStartTime } =
        usePerformanceMetricsForTable('Individual return');
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: isFilteredEnterAmountsEnabled } = useIsFeatureEnabled({ featureFlagName: FilteredEnterAmountsEnabled });
    const { isReportExecuting: isInTaxCalculationReportList, isReportPrinting, registerDependency } = useTaxReturnCalculationState();
    const {
        data: currentReport,
        isError: isErrorReport,
        isFetching: isFetchingReport,
        queryKey: queryKeyReport,
        error: reportError,
    } = useGetTaxReturnById<Report>(props.reportId);
    const hasReport = reportError?.response?.status !== 404;
    const {
        cachedWhitePaperReport: currentWhitePaperReport,
        loadWhitePaperReportDataCoordinated,
        updateCachedReportWithChanges,
        isLoading: isFetchingWhitePaperReport,
        availableVersions,
        latestCalculatedVersionId,
    } = useWhitePaperReportCache(props.reportId, {
        hasCompletedCalculation: currentReport?.executed === undefined ? undefined : Boolean(currentReport.executed),
    });
    const { showToast } = useToastMessage();
    const { setReturnDefaultsToForms } = useUserSettings();
    const { data: isAutoPostAmountsEnabled } = useIsFeatureEnabled({ featureFlagName: AutoPostAmountsEnabled });
    //commenting out for now but can be used to test later
    // const { data: isModernizationPreviewEnabled } = useIsFeatureEnabled({ featureFlagName: 'ModernizationPreviewEnabled' });
    // console.log('isModernizationPreviewEnabled:' + isModernizationPreviewEnabled);

    const [currentWhitePaperColumn, setCurrentWhitePaperColumn] = useState<IWhitePaperColumn | undefined>(undefined);
    const { setTraceCalculationData } = useDrillDown();
    const {
        rerunCalculationReport,
        isReportExecuting,
        isLoadingRecalculation: isFetchingRecalculation,
        isErrorRecalculation,
        taxReturnCalculationStatusList,
    } = useTaxReturnCalculationState();
    const {
        openForm,
        navigateToReport,
        showForm,
        tocData,
        hasTaxReturnTOCItems,
        isLoadingToc,
        loadTocData,
        setCurrentReport,
        setCurrentWhitePaperReport,
        setCommentMetadataVersion,
    } = useFormView();

    const wasLoadingTocRef = useRef<boolean>(false);
    useEffect(() => {
        if (wasLoadingTocRef.current && !isLoadingToc && !currentWhitePaperReport) {
            loadWhitePaperReportDataCoordinated(false);
        }
        wasLoadingTocRef.current = isLoadingToc;
    }, [isLoadingToc, currentWhitePaperReport, loadWhitePaperReportDataCoordinated]);

    const [isDrillDownVisible, { setTrue: showDrillDown, setFalse: hideDrillDown }] = useBoolean(false);
    const [isPillar2PanelOpen, { setTrue: openPillar2Panel, setFalse: closePillar2Panel }] = useBoolean(false);
    const { isPillar2Enabled } = usePillar2FeatureFlag();

    const gridComponentRef = useRef<GridComponent | null>(null);
    const selectableGridRef = useRef<ISelectableGrid | null>(null);
    const [isCommentsPanelOpen, setIsCommentsPanelOpen] = useState(false);
    const [commentData, setCommentData] = useState<{ rowData: IWhitePaperRow; columnName: string } | null | undefined>(undefined);
    const [currentCellArgs, setCurrentCellArgs] = useState<ICurrentCellEventArgs<IWhitePaperRow> | null>(null);
    const [changeTrackingData, setChangeTrackingData] = useState<IWhitePaperReportChanges>({});

    const {
        data: currentReportMetadata,
        queryKey: queryKeyCurrentReportMetadata,
        refetch: refetchReportMetadata,
    } = useGetMetadataForReport(props.reportId.toString());
    const { dataManager, contentLength } = useDataManager(
        currentWhitePaperReport,
        props.reportId.toString(),
        false,
        currentReportMetadata?.metadata
    );
    const isMultiColumns = isMultiColumnReport(currentWhitePaperReport);
    const isExecuting = isReportExecuting(props.reportId);
    const currentReturnStatus =
        taxReturnCalculationStatusList?.find((item: CalculationExecutionStatus) => item.taxReturnItemKey === props.reportId) || {};

    const validateErrorOnLoadingReportData = !isExecuting && !(isFetchingRecalculation && isErrorRecalculation);
    const commentsRouteDetails = useCellCommentsRouteDetails(currentWhitePaperReport);
    const { getFilteredEntities, setSelectedEntitiesSearchParams, selectedEntities } = useSelectedEntities();
    const entities: IFilterItem[] = getEntitiesFilterForColumns(currentWhitePaperReport?.columns || []);
    const filteredEntities: IFilterItem[] = getFilteredEntities(entities);
    const {
        setHasContextChangedFalse,
        hasContextChanged,
        setNewWarningMessage,
        clearWarningMessage,
        warningMessage,
        hasAmountChangedByAnotherUser,
        setHasAmountChangedByAnotherUserFalse,
    } = useTaxReturnEventHandler();

    const { getUtmParams, cleanUtmParamsFromUrl } = useUtmTracking();
    const { trackEvent, isReady } = useEventTracker();
    const hasPillar2Data: boolean =
        !!currentReport?.pillar2Settings && Object.getOwnPropertyNames(currentReport.pillar2Settings).length > 0;

    useEffect(() => {
        // For 'view' pages, track the return name as a custom parameter.
        const { isValid, params } = getUtmParams();
        if (params && isValid && currentReport && isReady) {
            trackEvent(REDIRECTED_EVENT_NAME, {
                action_name: REDIRECTED_ACTION_NAME,
                ...params,
                return_name_length: (currentReport?.name ?? '').length,
            });
            cleanUtmParamsFromUrl();
        }
    }, [getUtmParams, cleanUtmParamsFromUrl, trackEvent, isReady, currentReport?.name]);

    useEffect(() => {
        if (hasAmountChangedByAnotherUser) {
            setNewWarningMessage(true, t('recalculateNeeded').toString());
            setHasAmountChangedByAnotherUserFalse();
        }
    }, [hasAmountChangedByAnotherUser]);

    useEffect(() => {
        setNewWarningMessage(hasContextChanged, t('calculationContextChanged').toString());
    }, [hasContextChanged]);

    useEffect(() => {
        return () => {
            queryClient.resetQueries(queryKeyReport);
        };
    }, []);

    // Must also close the panel when this stops matching (not just open it when it matches):
    // opening pushes a history entry rather than replacing one, so browser back genuinely lands
    // on a distinct prior entry where this route no longer matches, and nothing else would notice
    // that to close the panel. Refetch the comment metadata on that path too - handleCommentsPanelClose
    // does this for the panel's own close button, but a route-driven close (e.g. browser back) bypasses it,
    // which otherwise leaves stale comment indicators on the grid after a comment is deleted.
    // Also re-run when the resolved column/row changes while staying on a comments route - openCommentsIfAllowed
    // only navigates (it no longer calls setCommentData directly), so this effect is the sole place commentData
    // gets set; keying it only on isCommentsSubroute meant navigating from one cell's comments straight to
    // another's (isCommentsSubroute staying true throughout) never picked up the new cell.
    useEffect(() => {
        if (commentsRouteDetails.isCommentsSubroute) {
            setCommentData(commentsRouteDetails.commentData);
        } else if (isCommentsPanelOpen) {
            handleRefetchMetadata();
            // Otherwise the closed cell's row/column stay keyed into useReportFieldCommentsDataSource's
            // query below, which keeps it enabled and subscribed indefinitely for a panel nobody is
            // looking at anymore.
            setCommentData(undefined);
        }
        setIsCommentsPanelOpen(commentsRouteDetails.isCommentsSubroute);
    }, [
        commentsRouteDetails.isCommentsSubroute,
        commentsRouteDetails.commentData?.columnName,
        commentsRouteDetails.commentData?.rowData?.rowNumber,
    ]);

    useEffect(() => {
        const executeRerun = () => {
            rerunCalculationReport(currentReport?.name, props.reportId, currentReport?.taxReturnItemId ?? '');
        };

        if (currentReport) {
            setCurrentReport(currentReport);
        }
        if (currentReport && shouldExecuteRerun(currentReport, isExecuting, isFetchingRecalculation)) {
            executeRerun();
        }
    }, [currentReport, props.reportId]);

    useEffect(() => {
        const fetchData = () => {
            resetStartTime();
        };

        if (currentReport && shouldFetch(currentReport, currentReturnStatus, hasContextChanged)) fetchData();
    }, [isExecuting, currentReport]);

    const closeDrillDown = (changeTrackingData: IReportDrillDownOnClose) => {
        const { amountsChangedOnServer, reportChanges } = changeTrackingData;
        hideDrillDown();
        setNewWarningMessage(amountsChangedOnServer, t('recalculateNeeded').toString());
        setChangeTrackingData(reportChanges);
        if (amountsChangedOnServer && currentWhitePaperReport) {
            updateCachedReportWithChanges(
                currentWhitePaperReport,
                reportChanges,
                amountsChangedOnServer,
                props.reportId,
                latestCalculatedVersionId
            ).catch(console.error);
        }
        if (selectableGridRef.current) {
            selectableGridRef.current.focusCell(undefined as unknown as IIndex);
            if (amountsChangedOnServer) {
                selectableGridRef.current.markAsEdited(currentCellArgs?.cellIndex!);
            }
        }
    };

    const cleanupCache = () => {
        // Note: queryKeyWhitePaperReport removed - using simplified cache approach
        queryClient.resetQueries(queryKeyReport);
        queryClient.resetQueries(queryKeyCurrentReportMetadata);
        setShouldBlockNavigation(false);
    };

    useEffect(() => {
        // Note: queryKeyWhitePaperReport dependency removed - using simplified cache approach
        // registerDependency(props.reportId, queryKeyWhitePaperReport);
        return cleanupCache();
    }, [props.reportId]);

    const editCell = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            if (selectableGridRef.current) {
                selectableGridRef.current.startEdit(eventArgs.cellIndex);
            }
        }
    };

    const openDrillDownIfAllowed = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            const columnIndex = eventArgs.cellIndex.cellIndex! - CLICKABLE_COLUMN_START_INDEX;
            const currentCol = currentWhitePaperReport?.columns?.[columnIndex];
            setCurrentWhitePaperColumn(currentCol);
            setTraceCalculationData([
                { displayRowNumber: TOTAL_ROW_NUMBER },
                createSelectedWhitePaperCell(eventArgs.data, getColumnFieldNameByIndex(columnIndex, currentWhitePaperReport)),
            ]);

            showDrillDown();
        }
    };

    const openCommentsIfAllowed = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            if (isDrillDownAllowed(eventArgs.cellIndex)) {
                // Navigate to the cell comments route rather than opening the panel via local state
                // (matching the forms tab's PdfContextualMenu.openCommentsIfAllowed) so the address bar
                // reflects which comment thread is open and the panel can be deep-linked/refreshed.
                // The existing commentsRouteDetails effect below picks up the route change and opens
                // the panel, so this is the single source of truth for "how does a URL change turn
                // into an open panel."
                const columnId = getColumnIdFromFieldName(eventArgs.fieldName);
                navigate({
                    pathname: `/view/${props.reportId}/cell/${encodeURIComponent(columnId)}/${eventArgs.data.displayRowNumber}/comments`,
                    search: location.search,
                });
            }
        }
    };

    const getColumnIdFromFieldName = useCallback((fieldName: string): string => {
        if (!fieldName.includes('.')) {
            return fieldName;
        }
        const { entityCode } = getDynamicFieldNameParts(fieldName);
        return entityCode;
    }, []);

    const columns: ColumnModel[] = useMemo(
        () => getReportLinesColumns(currentWhitePaperReport, t, frozenColumnHeaders, filteredEntities),
        [currentWhitePaperReport, t, filteredEntities]
    );
    useEffect(() => {
        setHasContextChangedFalse();
        clearWarningMessage();
        setNewWarningMessage(currentWhitePaperReport?.reCalculationRequired ?? false, t('recalculateNeeded').toString());
        setChangeTrackingData(currentWhitePaperReport?.changeTrackingData ?? {});
        currentWhitePaperReport && setCurrentWhitePaperReport(currentWhitePaperReport);
    }, [currentWhitePaperReport]);
    const isFetching = isFetchingReport || isFetchingWhitePaperReport || isFetchingRecalculation || !validateErrorOnLoadingReportData;
    const isDataReadyToDisplay = !isFetching && currentWhitePaperReport !== undefined;
    const currentReportIsCalculating = isInTaxCalculationReportList(props.reportId) || false;

    const isReportNotFound = !hasReport;
    const hasReportExecuted = Boolean(currentReport?.executed);
    // Zero rows only means "no usable report" once the report has actually been executed - a brand-new,
    // not-yet-calculated report also has zero rows but is perfectly valid and shouldn't force Forms.
    const hasNoReportRows =
        hasReport && !isErrorReport && hasReportExecuted && isDataReadyToDisplay && (currentWhitePaperReport?.rows?.length ?? 0) === 0;
    const needsFormsCheck = isReportNotFound || hasNoReportRows;
    const isReadyToEvaluate = isReportNotFound || isDataReadyToDisplay || isErrorReport;

    const [hasTriggeredTocCheck, setHasTriggeredTocCheck] = useState(false);
    const [hasResolvedActiveView, setHasResolvedActiveView] = useState(showForm);
    const openFormRef = useRef(openForm);
    openFormRef.current = openForm;
    const loadTocDataRef = useRef(loadTocData);
    loadTocDataRef.current = loadTocData;

    useEffect(() => {
        if (hasResolvedActiveView) {
            return;
        }

        if (showForm) {
            setHasResolvedActiveView(true);
            return;
        }

        if (!isReadyToEvaluate) {
            return;
        }

        if (!needsFormsCheck) {
            setHasResolvedActiveView(true);
            return;
        }

        if (!hasTriggeredTocCheck) {
            setHasTriggeredTocCheck(true);
            // Only checking whether this return has any forms at all here - skip the form/PDF
            // fetch loadTocData would otherwise trigger. Besides being wasted work for a return
            // that turns out to stay on Report, that fetch reads currentReport from FormViewContext
            // for the request's returnId, and this is the same render pass where ReportLinesTable
            // is first populating that context - racing it sends the request with an empty returnId.
            loadTocDataRef.current(undefined, { skipGetFormData: true });
            return;
        }

        if (isLoadingToc) {
            return;
        }

        if (hasTaxReturnTOCItems) {
            openFormRef.current({ replace: true });
        } else {
            setHasResolvedActiveView(true);
        }
    }, [hasResolvedActiveView, showForm, isReadyToEvaluate, needsFormsCheck, hasTriggeredTocCheck, isLoadingToc, hasTaxReturnTOCItems]);

    const location = useLocation();
    const isOnBareReportUrl = location.pathname === `${VIEW_REPORT_URL}${props.reportId}`;
    const navigate = useNavigate();
    // Matches the two shapes a field comment thread's URL can take (see useCellCommentsRouteDetails)
    // so closing the panel can navigate back off it rather than leaving the address bar pointing
    // at a comments subroute nothing is showing anymore.
    const cellCommentsFormsMatch = useMatch('/view/:reportId/forms/:tocItemId/cell/:columnId/:rowId/:subroute?/:cellCommentId?');
    const cellCommentsBareMatch = useMatch('/view/:reportId/cell/:columnId/:rowId/:subroute?/:cellCommentId?');

    useEffect(() => {
        if (!hasResolvedActiveView || isLoadingToc || !needsFormsCheck || !hasTaxReturnTOCItems) {
            return;
        }
        if (isOnBareReportUrl && !showForm) {
            openFormRef.current({ replace: true });
        }
    }, [hasResolvedActiveView, isLoadingToc, needsFormsCheck, hasTaxReturnTOCItems, isOnBareReportUrl, showForm]);

    // A return that's currently calculating already has a report by definition - let its own
    // "calculating" spinner (with Stop button, rendered inside CommonIndividualReportTable) show
    // immediately instead of sitting behind this generic spinner for the whole calculation.
    // Once it finishes, if it turns out needsFormsCheck is true for a return we're about to redirect
    // away from, keep this curtain up until that redirect (above) actually lands - otherwise the
    // now-empty report grid paints for a frame before the navigate takes effect.
    const isPendingFormsRedirect = hasResolvedActiveView && needsFormsCheck && hasTaxReturnTOCItems && isOnBareReportUrl && !showForm;
    // hasResolvedActiveView itself only flips a render tick after isReadyToEvaluate/needsFormsCheck
    // settle (it's driven by the effect above, not computed inline) - so for the common case where a
    // calculation finishes and definitely doesn't need Forms, don't wait for that tick: drop the curtain
    // as soon as we can tell for certain. Otherwise this remounts the whole resolved subtree (and
    // everything inside it, including the calculating spinner) back behind the curtain for one frame
    // right as the calculation completes, every time.
    const isDefinitelyStayingOnReport = isReadyToEvaluate && !needsFormsCheck;
    const shouldShowResolvingSpinner =
        (!hasResolvedActiveView && !currentReportIsCalculating && !isDefinitelyStayingOnReport) || isPendingFormsRedirect;

    const setReturnDefaultsToFormsRef = useRef(setReturnDefaultsToForms);
    setReturnDefaultsToFormsRef.current = setReturnDefaultsToForms;

    useEffect(() => {
        // Skip while data is unsettled (e.g. a recalculation in flight) - hasNoReportRows/needsFormsCheck
        // can otherwise transiently flip for a legitimately-empty report, flapping the Report tab and the
        // "recently opened" link between the report URL and the forms URL on every fetch.
        if (!hasResolvedActiveView || isLoadingToc || isFetching) {
            return;
        }
        setReturnDefaultsToFormsRef.current(props.reportId, needsFormsCheck && hasTaxReturnTOCItems);
    }, [hasResolvedActiveView, isLoadingToc, isFetching, needsFormsCheck, hasTaxReturnTOCItems, props.reportId]);

    const { queryKey: previousVersionsKey } = useGetWhitePaperReportPreviousVersions({
        ReportId: props.reportId,
    });

    useEffect(() => {
        registerDependency(props.reportId, queryKeyReport);
        registerDependency(props.reportId, previousVersionsKey);
    }, [previousVersionsKey]);

    const { popover: historyMenuPopover, item: historyMenuItem } = useHistoryMenuStyles();

    const compareHistoryButtonId = `compareHistoryButton`;
    const reportVersionsMenuList = (
        <MenuList aria-label={t('compareHistory').toString()}>
            <MenuGroup>
                <MenuGroupHeader>{t('datesRun').toString()}</MenuGroupHeader>
                {isResponseArrayValid(availableVersions) &&
                    availableVersions?.map((version) => (
                        <MenuItem
                            key={version.versionId}
                            className={historyMenuItem}
                            data-analytics-button-name='compare_history_menu_item'
                            onClick={() => props.navigateToCompareReport(version.versionId ?? '', selectedEntities)}
                        >
                            {getReportVersionLabelText(version)}
                        </MenuItem>
                    ))}
            </MenuGroup>
        </MenuList>
    );

    const rerunCalculation = async () => {
        navigateToReport();
        if (isAutoPostAmountsEnabled && currentReport?.updateBackReport && !functionalAccess?.postAmountEnabled) {
            showToast(
                t('calculationInProgress').toString(),
                <span>{t('amountsNotPostedDueToInsufficientPermissions').toString()}</span>,
                'info'
            );
        }

        rerunCalculationReport(currentReport?.name, props.reportId, currentReport?.taxReturnItemId ?? '', undefined, (_data: any) => {
            clearWarningMessage();
            setHasContextChangedFalse();
            registerDependency(props.reportId, queryKeyReport);
        });
    };

    const getCompareButton: () => JSX.Element = () => {
        if (!availableVersions?.length) {
            return <></>;
        }

        const compareHistoryLabel = t('compareHistory').toString();

        return (
            <Menu key='compareHistoryButton'>
                <MenuTrigger disableButtonEnhancement>
                    <MenuButton
                        id={compareHistoryButtonId}
                        aria-label={compareHistoryLabel}
                        icon={<History20Regular />}
                        disabled={isFetching || currentReportIsCalculating /*|| isViewFormPdfProcessing*/}
                    >
                        {compareHistoryLabel}
                    </MenuButton>
                </MenuTrigger>
                <MenuPopover className={historyMenuPopover}>{reportVersionsMenuList}</MenuPopover>
            </Menu>
        );
    };

    const compareButton: JSX.Element = getCompareButton();

    const sections = useMemo(() => {
        const statements = getFilteredTOCItemsByCondition(tocData?.items ?? [], (item) => item.isStatement ?? false);

        return getSectionsFilterItem(currentWhitePaperReport?.rows || [], statements || []);
    }, [currentWhitePaperReport, tocData]);

    const { programmaticallyScrollToRow } = useGridScroll();
    const onSectionsScrollToChange = (filteredItems: IFilterItem[]) => {
        if (gridComponentRef && gridComponentRef.current) {
            const rowId = parseInt(filteredItems[0].key, 10);
            const rowIndex = currentWhitePaperReport?.rows?.findIndex((row) => row.rowNumber === rowId);
            programmaticallyScrollToRow(gridComponentRef.current, { rowIndex: rowIndex });
        }
    };
    const { isSearchValueApplied, clearSearch } = useSearchTable();
    const [searchEntityValue, setSearchEntityValue] = useState<string>('');
    const updateVisibleColumns = (filteredItems: IFilterItem[]) => {
        if (gridComponentRef && gridComponentRef.current && currentWhitePaperReport) {
            const columns = getVisibleEntityColumnNames(filteredItems, currentWhitePaperReport?.columns ?? []);
            updateSubtotalByEntitiesValue(currentWhitePaperReport?.rows || [], columns, gridComponentRef);
        }
    };
    const onFilterChange = (filteredItems: IFilterItem[]) => {
        setSelectedEntitiesSearchParams(filteredItems);
        updateVisibleColumns(filteredItems);
    };
    const handlerSetCheckedItems = (filterItems: IFilterItem[]) => {
        onFilterChange(filterItems);
        clearSearch(gridComponentRef);
    };

    const getTablePerformanceInformation = () => ({
        rowCount: contentLength,
        columnCount: columns.length,
        visibleColumnCount: columns.filter((column) => column.visible).length,
        reportId: props.reportId,
        isMultiColumn: isMultiColumns,
    });

    const renderFinished = () => {
        trackRenderFinished(getTablePerformanceInformation());
    };

    const onGridDataBound = () => {
        trackScrollFinished(getTablePerformanceInformation());
    };

    const filterGroupItems: React.ReactNode[] = [
        <ScrollToSectionDropdown
            sections={sections}
            label='goToSection'
            onSectionChange={onSectionsScrollToChange}
            isDisabled={isSearchValueApplied}
            key='scrollToSectionDropdown'
        />,
        isMultiColumns ? (
            <FilterButton
                data-testid='filterButton'
                filterName='entityName'
                items={entities}
                label='entity'
                checkedItems={filteredEntities}
                setCheckedItems={handlerSetCheckedItems}
                searchValue={searchEntityValue}
                setSearchValue={setSearchEntityValue}
                noElementsFoundtext={t('noEntitiesFound')}
                key='entityFilterButton'
            />
        ) : null,
        <GridSearchBar searchBoxPlaceholderText={t('search')} key='gridSearchBar' />,
        compareButton,
    ];
    const wrapperStyles = useWrapperStyles();
    const hasCalculationOptions = currentWhitePaperReport?.options?.length !== undefined && currentWhitePaperReport?.options?.length > 0;

    const hasUsableReport = hasReport && !(hasNoReportRows && hasTaxReturnTOCItems);

    const actionBarButtons = ActionBarButtons({
        isReportActive: !showForm,
        handleOpenPathToPortal: openForm,
        navigateToReport,
        hasForms: hasTaxReturnTOCItems,
        hasReport: hasUsableReport,
        isLoading: isLoadingToc || !isDataReadyToDisplay,
    });

    const calculateButton = CalculateButton({
        onClick: rerunCalculation,
        disabled: isFetching || currentReportIsCalculating || isReportPrinting(props.reportId),
    });

    const calculateAndPrintDisabled = isFetching || currentReportIsCalculating || isReportPrinting(props.reportId);
    const pillar2Button =
        isPillar2Enabled && hasPillar2Data ? (
            <Pillar2ReportButton onClick={openPillar2Panel} disabled={calculateAndPrintDisabled} isPanelOpen={isPillar2PanelOpen} />
        ) : undefined;

    const [enterAmountsDialogProps, setEnterAmountsDialogProps] = useState<IEnterAmountsDialogProps | null>(null);
    const openEnterAmountsIfAllowed = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            const columnIndex = eventArgs.cellIndex.cellIndex! - CLICKABLE_COLUMN_START_INDEX;
            const currentColumn = currentWhitePaperReport?.columns?.[columnIndex];
            const isInput = isInputRow(eventArgs.data);
            const rowDescription =
                isInput && isFilteredEnterAmountsEnabled
                    ? `Row ${eventArgs.data.description}, ${currentColumn?.entityCode} ${currentColumn?.entityName} `
                    : '';

            setEnterAmountsDialogProps({
                //buttonTrigger: eventArgs as HTMLElement,
                rowDescription: rowDescription,
                columnId: currentColumn?.originalColumnId,
                drillDownRowNumber: Number.parseInt(eventArgs.data.displayRowNumber!),
                onClose: () => setEnterAmountsDialogProps(null),
                entity: currentColumn?.entityCode ?? currentReport!.entityCode!,
                isInputRow: isInput,
            } as unknown as IEnterAmountsDialogProps);
        }
    };

    const onEnterAmountsClosedCallback: (amountsChangedOnServer: boolean, updatedChangeTrackingData: IWhitePaperReportChanges) => void = (
        amountsChangedOnServer: boolean,
        updatedChangeTrackingData: IWhitePaperReportChanges
    ) => {
        setEnterAmountsDialogProps(null);
        setNewWarningMessage(amountsChangedOnServer, t('recalculateNeeded').toString());
        setChangeTrackingData(updatedChangeTrackingData);
        if (amountsChangedOnServer && currentWhitePaperReport) {
            updateCachedReportWithChanges(
                currentWhitePaperReport,
                updatedChangeTrackingData,
                amountsChangedOnServer,
                props.reportId,
                latestCalculatedVersionId
            );
        }

        if (selectableGridRef.current) {
            selectableGridRef.current.focusCell(undefined as unknown as IIndex);
            if (changeTrackingData !== updatedChangeTrackingData) {
                selectableGridRef.current.markAsEdited(currentCellArgs?.cellIndex!);
                gridComponentRef?.current?.refresh();
                selectableGridRef.current.refreshGrid();
            }
        }
    };

    const { functionalAccess } = useFunctionalAccess();
    const isCollaborationUIEnabled = useIsCollaborationUIEnabled();

    const handleCommentsPanelClose = () => {
        handleRefetchMetadata();
        const item = document.querySelector('.item-last-focused') as HTMLElement;
        setFocusOnSyncfusionGridCell(item);
        setIsCommentsPanelOpen(false);
        // isCommentsPanelOpen is already false by the time the navigate() below lands and the route
        // effect notices, so its own else-branch cleanup won't fire for this path - clear it here too.
        setCommentData(undefined);

        // The panel was opened by navigating to a /cell/.../comments URL (see PdfContextualMenu
        // and the report grid's own comments context menu item) - closing it via the panel's own
        // close button doesn't navigate anywhere on its own, so without this the address bar (and
        // a later browser-back press) would still point at a comments subroute nothing is
        // showing. replace: true so closing doesn't add its own back-button stop.
        if (cellCommentsFormsMatch) {
            navigate(
                {
                    pathname: `/view/${cellCommentsFormsMatch.params.reportId}/forms/${cellCommentsFormsMatch.params.tocItemId}`,
                    search: location.search,
                },
                { replace: true }
            );
        } else if (cellCommentsBareMatch) {
            navigate({ pathname: `/view/${cellCommentsBareMatch.params.reportId}`, search: location.search }, { replace: true });
        }
    };

    const handleRefetchMetadata = () => {
        refetchReportMetadata();
    };

    // Create data source and metadata for field-level comments using new hooks
    const fieldDataSource = useReportFieldCommentsDataSource(
        props.reportId.toString(),
        commentData?.rowData?.rowNumber?.toString() ?? '',
        encodeURIComponent(commentData?.columnName ?? ''),
        currentReport?.name ?? undefined
    );
    // Resolved by columnName rather than carried on commentData, so the title is correct
    // regardless of how the panel was opened (grid right-click, forms deep link, or refresh).
    const commentColumnTitle = commentData?.columnName
        ? columns.find((column) => getColumnIdFromFieldName(column.field ?? '') === commentData.columnName)?.headerText
        : undefined;
    const fieldMetadata = useReportFieldCommentsMetadata(
        props.reportId.toString(),
        currentReport?.name ?? undefined,
        commentData?.rowData?.rowNumber,
        encodeURIComponent(commentData?.columnName ?? ''),
        commentData?.rowData?.description ?? undefined,
        commentColumnTitle
    );

    /* in order to have the grid reflect updates to the metadata after it is data bound, we have to invoke refresh */
    useEffect(() => {
        if (selectableGridRef.current) {
            selectableGridRef.current.refreshGrid();
        }
        // currentWhitePaperReport's cells are mutated in place when metadata resolves (see
        // useDataManager/DataManagerUtils), so its reference never changes - the form viewer's
        // comment indicators key their own refresh off this counter instead.
        setCommentMetadataVersion((version) => version + 1);
    }, [currentReportMetadata, setCommentMetadataVersion]);

    return (
        <div className={wrapperStyles.reportLinesWrapper}>
            {shouldShowResolvingSpinner && (
                <div data-testid='reportLinesResolvingActiveView' className={wrapperStyles.resolvingSpinner}>
                    <LoadingSpinner id='loadingSpinner' label={t('loadingPleaseWait').toString()} labelPosition='below' />
                </div>
            )}
            {!shouldShowResolvingSpinner && (
                <>
                    {isCollaborationUIEnabled && (
                        <CommentsPanelWrapper
                            dataSource={fieldDataSource}
                            metadata={fieldMetadata}
                            isOpen={isCommentsPanelOpen}
                            onClose={handleCommentsPanelClose}
                            isCollaborationEnabled={isCollaborationUIEnabled}
                        />
                    )}
                    <GridStateContextProvider grid={gridComponentRef}>
                        <CommonIndividualReportTable
                            key={props.reportId}
                            taxReturnId={currentReport?.taxReturnItemId}
                            columns={columns}
                            currentReport={currentWhitePaperReport}
                            isError={isErrorReport}
                            isTableDataReadyToDisplay={isDataReadyToDisplay}
                            componentRef={selectableGridRef}
                            dataManager={dataManager}
                            currentCellArgs={currentCellArgs}
                            setCurrentCellArgs={setCurrentCellArgs}
                            onRenderFinished={renderFinished}
                            onScrollingStarted={trackScrollStarted}
                            onGridDataBound={onGridDataBound}
                            handleRefetchMetadata={handleRefetchMetadata}
                            contextMenuProps={
                                {
                                    enterAmounts: {
                                        getVisibilityState: (rowData: IRowInfo<IWhitePaperRow>) => {
                                            if (
                                                isDrillDownContextMenuAllowed(
                                                    rowData,
                                                    isMultiColumns,
                                                    currentWhitePaperReport,
                                                    isDrillDownAllowed
                                                )
                                            ) {
                                                if (functionalAccess?.amountDataEntryAccessEnabled) {
                                                    return ContextMenuItemDisplayState.Enabled;
                                                }
                                                return ContextMenuItemDisplayState.Disabled;
                                            }
                                            return ContextMenuItemDisplayState.Invisible;
                                        },
                                        onMenuItemClick: openEnterAmountsIfAllowed,
                                    } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                                    drillDown: {
                                        getVisibilityState: (rowData: IRowInfo<IWhitePaperRow>) => {
                                            if (
                                                isDrillDownContextMenuAllowed(
                                                    rowData,
                                                    isMultiColumns,
                                                    currentWhitePaperReport,
                                                    isDrillDownAllowed
                                                )
                                            ) {
                                                if (functionalAccess?.amountDataEntryAccessEnabled) {
                                                    return ContextMenuItemDisplayState.Enabled;
                                                }
                                                return ContextMenuItemDisplayState.Disabled;
                                            }
                                            return ContextMenuItemDisplayState.Invisible;
                                        },
                                        onMenuItemClick: openDrillDownIfAllowed,
                                    } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                                    comments: {
                                        getVisibilityState: (rowInfo: IRowInfo<IWhitePaperRow>) => {
                                            const index = { ...rowInfo } as IIndex;
                                            if (isCollaborationUIEnabled && isDrillDownAllowed(index)) {
                                                return ContextMenuItemDisplayState.Enabled;
                                            }
                                            return ContextMenuItemDisplayState.Invisible;
                                        },
                                        onMenuItemClick: openCommentsIfAllowed,
                                    } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                                    editCell: {
                                        getVisibilityState: (_rowInfo: IRowInfo<IWhitePaperRow>) => {
                                            return ContextMenuItemDisplayState.Invisible;
                                        },
                                        onMenuItemClick: editCell,
                                    } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                                } as ICommonIndividualReportTableContextMenuProps
                            }
                            reportId={props.reportId}
                            calculateButton={calculateButton}
                            calculationDiagnosticsButton={
                                <CalculationDiagnosticsButton reportId={props.reportId} returnName={currentReport?.name ?? undefined} />
                            }
                            pillar2Button={pillar2Button}
                            actionBar={actionBarButtons}
                            filterGroupItems={filterGroupItems}
                            clearAllFilters={() => handlerSetCheckedItems([])}
                            shouldDisplayCalculationOptions={hasCalculationOptions}
                            loadingIndicator={{ indicatorType: 'Shimmer' }}
                            headerWarningMessage={warningMessage}
                            isCollaborationEnabled={isCollaborationUIEnabled}
                        />
                    </GridStateContextProvider>
                    {isPillar2Enabled && hasPillar2Data && (
                        <Pillar2Panel
                            isOpen={isPillar2PanelOpen}
                            onClose={closePillar2Panel}
                            reportId={props.reportId}
                            pillar2Settings={currentReport?.pillar2Settings!}
                        />
                    )}
                    {isDrillDownVisible && (
                        <ReportDrillDown
                            onClose={closeDrillDown}
                            report={currentReport!}
                            showPanel={isDrillDownVisible}
                            whitePaperReportMap={currentWhitePaperReport?.rowsMap as IWhitePaperMappedRowDictionary}
                            whitePaperReportColumn={currentWhitePaperColumn}
                            changeTrackingData={changeTrackingData}
                            isMultiColumnWhitePaperReport={isMultiColumns}
                        />
                    )}
                    {enterAmountsDialogProps && (
                        <EnterAmountsDialog
                            {...enterAmountsDialogProps}
                            case={currentReport!.case!}
                            jurisdiction={currentReport!.jurisdiction!}
                            period={currentReport!.year!.toString()}
                            adjustmentCode=''
                            location={currentReport!.location!}
                            changeTrackingData={changeTrackingData}
                            taxReturnKey={currentReport!.id!}
                            onClose={onEnterAmountsClosedCallback}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export { ReportLinesTable as default };
